import type { IBoundarySchoolLink } from "@/shared/types/boundary-school-link.types";

import type {
  IDraftBoundaryGeometryRequest,
} from "../custom-layers/custom-layers.interface";

export type { IBoundarySchoolLink };

export enum ImportType {
  SCHOOL_LIST = "schoolList",
  CAPACITY = "capacity",
  ATTENDANCE_ZONES = "attendanceZones",
  DISTRICT_BOUNDARY = "districtBoundary",
  NEW_HOUSING = "newHousing",
}

export enum SchoolLevelType {
  ELEMENTARY = "ELEMENTARY",
  MIDDLE = "MIDDLE",
  HIGH = "HIGH",
  K8 = "K8",
}

export type MyDistrictSchoolStatus = "OPEN" | "CLOSED";

export interface IMyDistrictSchoolListItem {
  id: string;
  internalId: string;
  name: string | null;
  abbreviation: string | null;
  educationalProgramType: string | null;
  level: SchoolLevelType | null;
  /** Ordered like map school payload; use with district levels for display labels. */
  schoolLevelIds: string[];
  lowestGradeServed: number | null;
  highestGradeServed: number | null;
  address: string | null;
  principalName: string | null;
  status: MyDistrictSchoolStatus;
  hasStudents: boolean;
}

export interface IMyDistrictSchoolsResponse {
  items: IMyDistrictSchoolListItem[];
}

export interface IGetMyDistrictSchoolsParams {
  districtId: string;
  search?: string;
  schoolLevels?: SchoolLevelType[];
  educationalProgramTypes?: string[];
  statuses?: MyDistrictSchoolStatus[];
}

export interface IImportFileExtensionsResponse {
  importType: ImportType;
  extensions: string[];
}

export interface ICreateUploadUrlRequest {
  filename: string;
  mimetype: string;
  size: number;
  districtId: string;
}

export interface IUploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  expiresIn: number;
}

export interface IImportSchoolListRequest {
  fileId: string;
  districtId: string;
}

/** Single validation issue for a field (no row repetition). */
export interface IImportIssue {
  field?: string;
  message: string;
  code?: string;
}

/** All validation issues for one row (row number shown once). */
export interface IImportRowError {
  row: number;
  studentId?: string;
  errors: IImportIssue[];
}

export interface IFileInfo {
  id?: string;
  name: string;
  url?: string;
}

export interface IImportSchoolListResponse {
  totalRows: number;
  successCount: number;
  createdCount: number;
  updatedCount: number;
  errorCount: number;
  errors?: IImportRowError[];
  file: IFileInfo;
  processedAt: string;
  schoolDataImportId?: string | null;
  documentId?: string | null;
}

export interface IFinalizeSchoolDataImportRequest {
  documentName: string;
}

export interface IFinalizeSchoolDataImportResponse {
  success: boolean;
}

export type IImportSchoolCapacityRequest = IImportSchoolListRequest;
export type IImportSchoolCapacityResponse = IImportSchoolListResponse;

export interface ISchoolLevelResponse {
  id: string;
  districtId: string;
  type: SchoolLevelType;
  lowestGrade: number;
  highestGrade: number;
  scheduledMergeDate: string | null;
  hasQueuedImmediateMerge: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IAddAttendanceBoundaryArchiveRequest {
  districtId: string;
  zipFileId: string;
}

export interface IRemoveAttendanceBoundaryArchiveRequest {
  districtId: string;
  zipFileId: string;
}

export interface IImportAttendanceBoundariesRequest {
  districtId: string;
  zipFileIds: string[];
}

export interface IImportAttendanceBoundariesDocumentResult {
  documentId: string;
  zipFileId: string;
  fileName?: string;
  totalPolygons: number;
  connectedCount: number;
  notConnectedCount: number;
  error?: string;
}

export interface IImportAttendanceBoundariesResponse {
  documents: IImportAttendanceBoundariesDocumentResult[];
  connectedBySchoolLevel: Partial<Record<SchoolLevelType, number>>;
  notConnectedTotal: number;
  processedAt: string;
}

export interface IFinalizeAttendanceBoundariesDocumentItem {
  documentId: string;
  documentName: string;
}

export interface IFinalizeAttendanceBoundariesBatchRequest {
  districtId: string;
  documents: IFinalizeAttendanceBoundariesDocumentItem[];
}

export interface IFinalizeAttendanceBoundariesBatchDocumentResult {
  documentId: string;
  documentName: string;
  districtId: string;
  processedAt: string;
  updatedAt: string;
}

export interface IFinalizeAttendanceBoundariesBatchResponse {
  documents: IFinalizeAttendanceBoundariesBatchDocumentResult[];
}

export interface IUpsertSchoolBoundaryPreviewItemRequest {
  id?: string | null;
  documentId: string;
  geometry: GeoJSON.Geometry;
  /** Omit to keep current links, send [] to unassign. */
  schools?: IBoundarySchoolLink[];
  deleted?: boolean;
}

export interface IUpsertSchoolBoundaryPreviewsRequest {
  districtId: string;
  boundaries: IUpsertSchoolBoundaryPreviewItemRequest[];
}

export interface ISchoolBoundaryPreviewResponse {
  id: string;
  documentId: string;
  geometryGeoJson: string;
  color?: string | null;
  schools: IBoundarySchoolLink[];
}

export interface ISchoolBoundaryPreviewDocumentStatsResponse {
  documentId: string;
  connectedCount: number;
  notConnectedCount: number;
  totalCount: number;
}

export interface ISchoolBoundaryPreviewListResponse {
  items: ISchoolBoundaryPreviewResponse[];
  documentStats?: ISchoolBoundaryPreviewDocumentStatsResponse;
}

export interface IGetAttendanceBoundaryPreviewsParams {
  districtId: string;
  documentIds: string[];
  schoolLevelType?: SchoolLevelType;
}

export interface IGetAttendanceBoundaryPreviewGeometriesParams {
  districtId: string;
  documentIds: string[];
  schoolLevelType: SchoolLevelType;
  cursor?: string;
  limit?: number;
}

export interface IGetUnassignedAttendanceBoundaryPreviewsParams {
  districtId: string;
  documentIds: string[];
}

export interface ISchoolBoundaryPreviewUnassignedListResponse {
  items: ISchoolBoundaryPreviewResponse[];
  totalCount: number;
}

export interface IGetUnassignedAttendanceBoundaryPreviewCountParams {
  districtId: string;
  documentIds: string[];
}

export interface ISchoolBoundaryPreviewUnassignedCountResponse {
  notConnectedCount: number;
}

export interface ISchoolBoundaryPreviewDetailsGrades {
  lowestGradeLabel: string | null;
  highestGradeLabel: string | null;
}

export interface ISchoolBoundaryPreviewDetails extends ISchoolBoundaryPreviewResponse {
  schoolTypes: SchoolLevelType[];
  grades?: ISchoolBoundaryPreviewDetailsGrades | null;
  address?: string | null;
  color?: string | null;
}

export interface IGetAttendanceBoundaryPreviewDetailsParams {
  previewId: string;
}

export interface ISchoolResponse {
  id: string;
  districtId: string;
  internalId: string;
  name: string | null;
  abbreviation: string | null;
  allowProjections: boolean;
  contactEmail: string | null;
  addressId: string | null;
  lowestGradeServed: number | null;
  highestGradeServed: number | null;
  /** Linked level ids (stable sort; empty if none). */
  schoolLevelIds: string[];
  principalName: string | null;
  phoneNumber: string | null;
  schoolUrl: string | null;
  educationalProgramType: string | null;
  showOnFindMySchool: boolean;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ISchoolAddressResponse {
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface ISchoolScheduledGradeRangeChange {
  id: string;
  lowestGradeServed: number;
  highestGradeServed: number;
  schoolLevelTypes: SchoolLevelType[];
  startDate: string;
}

export interface ISchoolForEditResponse {
  school: ISchoolResponse;
  address: ISchoolAddressResponse | null;
  scheduledGradeRangeChanges?: ISchoolScheduledGradeRangeChange[];
  finalClosingDate?: string | null;
  currentProjectionRange?: { startYear: number; endYear: number } | null;
}

export interface ISchoolHasStudentsResponse {
  hasStudents: boolean;
}

export interface ISchoolInternalIdAvailabilityResponse {
  available: boolean;
}

export interface ISchoolInternalIdAvailabilityRequest {
  districtId: string;
  internalId: string;
  excludeSchoolId?: string;
}

export interface IUpdateSchoolRequest {
  name?: string;
  abbreviation?: string | null;
  internalId?: string;
  lowestGradeServed?: number;
  highestGradeServed?: number;
  schoolLevelTypes?: SchoolLevelType[];
  principalName?: string | null;
  phoneNumber?: string | null;
  contactEmail?: string | null;
  schoolUrl?: string | null;
  allowProjections?: boolean;
  showOnFindMySchool?: boolean;
  address?: { street: string; city: string; state: string; zip: string } | null;
  /** Full pending list; existing items carry their id. */
  scheduledGradeRangeChanges?: Array<Omit<ISchoolScheduledGradeRangeChange, "id"> & { id?: string }>;
  /** Honoured for platform admins only. */
  regenerateProjections?: boolean;
}

export interface ICreateSchoolRequest extends IUpdateSchoolRequest {
  districtId: string;
  internalId: string;
}

export interface ISchoolLastActionResponse {
  id: string;
  actionType: string;
  payload: { date?: string; reason?: string };
  createdAt: string;
}

export interface ISchoolDocumentInfoResponse {
  id: string;
  fileName: string;
  fileSizeBytes: number;
  type: string;
  title: string;
  description: string | null;
  createdAt: string;
  fileId: string;
  mimeType: string;
  processingState: "PENDING_CONFIRMATION" | "FINALIZING" | "FINISHED";
}

export interface ISchoolBoundaryGeometry {
  type: string;
  coordinates: unknown;
}

export interface ISchoolBoundaryResponse {
  id: string;
  documentId: string | null;
  createdAt: string;
  updatedAt: string;
  geometry: ISchoolBoundaryGeometry;
  schools: IBoundarySchoolLink[];
}

export interface ISchoolDriveFileInfoResponse {
  id: string;
  name: string;
  fileId: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedAt: string;
}

export interface ISchoolWithDetailsResponse {
  school: ISchoolResponse;
  address: string | null;
  capacityByYear: Record<number, number>;
  documents: ISchoolDocumentInfoResponse[];
  driveFiles: ISchoolDriveFileInfoResponse[];
  lastAction: ISchoolLastActionResponse | null;
  futureAction: ISchoolLastActionResponse | null;
  schoolBoundaries: ISchoolBoundaryResponse[];
  color: string;
}

export interface ISchoolDatesOfOperationPeriodResponse {
  id: string;
  startDate: string;
  endDate: string | null;
}

export interface ISchoolDatesOfOperationResponse {
  items: ISchoolDatesOfOperationPeriodResponse[];
}

export interface ISchoolProgramPeriodResponse {
  id: string;
  startAt: string;
  endAt: string | null;
}

export interface ISchoolProgramResponse {
  id: string;
  nameId: number;
  name: string | null;
  programType: string;
  metadata: Record<string, unknown>;
  periods: ISchoolProgramPeriodResponse[];
  studentRecordIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ISchoolProgramMetadataRequest {
  language?: string;
  lowestGradeServed?: number;
  highestGradeServed?: number;
}

export interface ISchoolProgramUpsertRequest {
  programType: string;
  name?: string | null;
  metadata: ISchoolProgramMetadataRequest;
  periods: { startAt: string; endAt?: string | null }[];
  studentRecordIds: string[];
}

export interface IUpsertSchoolProgramsRequest {
  programs: ISchoolProgramUpsertRequest[];
}

export interface IUpsertSchoolProgramsResponse {
  programIds: string[];
}

export interface IUpsertSchoolDatesOfOperationRequest {
  startDate: string;
  endDate?: string | null;
}

export type SchoolCapacityOtherYearsOrder = "upcomingAndPast" | "upcoming" | "past";

export interface ISchoolCapacityByYearResponse {
  capacityByYear: Record<number, number>;
}

export interface IUpsertSchoolCapacityOtherYearsRequest {
  order: SchoolCapacityOtherYearsOrder;
  yearsCount?: number;
}

export interface IUpsertSchoolCapacityRequest {
  year: number;
  capacity: number;
  otherYears?: IUpsertSchoolCapacityOtherYearsRequest | null;
}

export interface IExportSchoolBoundariesZipArg {
  districtId: string;
  schoolLevelType: SchoolLevelType;
}

export interface IDownloadSchoolBoundariesZipSuccessResult {
  success: true;
  filename: string;
}

export interface IExportMyDistrictSchoolsArg {
  districtId: string;
}

export interface IDownloadMyDistrictSchoolsSuccessResult {
  success: true;
  filename: string;
}

export type {
  GapFillFailureReason,
  IDraftBoundaryGeometryRequest,
  IGapFillDraftOverrides,
  IResolveGapFillResponse,
  IResolveAutoFitResponse,
} from "../custom-layers/custom-layers.interface";
export interface IResolveSchoolBoundaryPreviewGapFillRequest {
  documentId: string;
  schoolLevelType: SchoolLevelType;
  lng: number;
  lat: number;
  draftBoundaries?: IDraftBoundaryGeometryRequest[];
  deletedBoundaryIds?: string[];
}

export interface IResolveSchoolBoundaryPreviewAutoFitRequest {
  documentId: string;
  schoolLevelType: SchoolLevelType;
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  editingBoundaryId: string;
  draftBoundaries?: IDraftBoundaryGeometryRequest[];
  deletedBoundaryIds?: string[];
}
